import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, useDocTitle } from "../components/Reveal";
import { MediaImg } from "../components/MediaImg";
import { CTABanner } from "../components/CTABanner";
import { PROJECTS, VIDEOS } from "../data/site";
import { CaseStudyModal } from "../components/CaseStudyModal";

const FILTERS = ["All", "Video", "Marketing", "Social", "Photography", "Branding", "Studio Builds", "Events"];

export default function Work() {
  useDocTitle("Work");
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const shown = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="work-page">
      <PageHero
        eyebrow="Selected Work"
        lines={[<>Work that <span className="text-gold-dark">worked.</span></>]}
        sub="Commercials, content engines, brand builds and studio builds from across Wisconsin — every project measured by the outcome it was hired to produce."
      />

      {/* SHOWREEL */}
      <section className="mx-auto max-w-7xl px-4 pb-14 sm:px-8 lg:px-16">
        <Reveal>
          <div className="relative overflow-hidden shadow-2xl">
            <video
              src={VIDEOS.showcase}
              poster="/assets/media/poster-showcase.jpg"
              autoPlay
              muted
              loop
              playsInline
              className="h-[280px] w-full object-cover sm:h-[520px]"
              data-testid="work-showreel-video"
            />
            <p className="absolute bottom-4 left-4 bg-night/50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-sand backdrop-blur-sm">
              Work Showcase · Real client work, on repeat
            </p>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 lg:px-16">
        <div className="flex flex-wrap gap-3" role="tablist" aria-label="Filter work">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                filter === f
                  ? "border-gold bg-gold text-ink"
                  : "border-ink/15 text-ink/60 hover:border-gold hover:text-gold-dark"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {shown.map((p, i) => (
              <motion.button
                layout
                key={p.id}
                data-testid={`work-card-${p.id}`}
                onClick={() => setActive(p)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative overflow-hidden text-left shadow-md ${
                  i % 5 === 0 ? "sm:col-span-2" : ""
                }`}
              >
                <MediaImg
                  src={p.image}
                  alt={p.title}
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                    i % 5 === 0 ? "h-72 sm:h-96" : "h-72"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-night/85 via-night/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                    {p.category} <span className="text-sand/40">·</span>
                    <MapPin size={11} /> {p.location}
                  </p>
                  <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-sand sm:text-2xl">
                    {p.title}
                  </h3>
                  <p className="mt-2 max-h-0 overflow-hidden text-sm text-sand/70 transition-all duration-500 group-hover:max-h-20">
                    {p.summary}
                  </p>
                </div>
                <span className="absolute right-5 top-5 border border-sand/30 bg-night/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-sand opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
                  View case
                </span>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <AnimatePresence>
        {active && <CaseStudyModal project={active} onClose={() => setActive(null)} />}
      </AnimatePresence>

      <CTABanner title="Like what you see? Let's build something." sub="Bring us the outcome you need — or just the idea. We'll bring the plan, the team, and the proof." />
    </div>
  );
}
